// Field adapters — backend (snake_case) ↔ frontend (camelCase/custom)

const str  = (v) => (v == null ? null : String(v));
const num  = (v) => (v == null ? 0 : parseFloat(v) || 0);
const bool = (v) => Boolean(v);
const isoDate = (v) => (v ? String(v).slice(0, 10) : '');

function fmtDateLabel(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  return new Date(s + 'T00:00').toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

// ── Vehicles ──────────────────────────────────────────────────────────────────
export function vehicleFromApi(v) {
  return {
    id:           str(v.id),
    _backendId:   v.id,
    name:         v.name || '',
    plate:        v.plate_number || '',
    type:         v.type_name_snapshot || v.vehicle_type?.name || '',
    typeId:       str(v.vehicle_type_id),
    groupId:      str(v.vehicle_group_id),
    agreementId:  str(v.agreement_template_id),
    currentAreaId: str(v.current_area_id),
    dropoffAreaIds: v.dropoff_area_ids || 'all',
    icon:         v.icon || '🚗',
    status:       v.status || 'available',
    seats:        v.seats_or_capacity || '',
    transmission: v.transmission || '',
    fuelType:     v.fuel_type || '',
    features:     v.features || '',
    mileage:      v.mileage_policy || 'unlimited',
    priceDay:     num(v.price_day),
    confirm:      v.confirm_mode || 'manual',
    deposit:      num(v.deposit_default),
    odometer:     v.odometer || 0,
    motExpiry:    isoDate(v.mot_expiry_date),
    bookPage:     bool(v.is_bookable_online),
    useGroupPricing: bool(v.use_group_pricing),
    insuranceMode:   v.insurance_mode || 'standard',
    customInsurance: v.custom_insurance_plans || v.customInsurancePlans || [],
    photos:       (v.photos || []).map(p => (typeof p === 'string' ? p : p.url || p.path || '')),
    mapX:         v.map_x ?? null,
    mapY:         v.map_y ?? null,
    speedLabel:   v.map_speed_label || '',
    headingLabel: v.map_heading_label || '',
    externalCode: v.external_code || '',
  };
}

export function vehicleToApi(v) {
  return {
    name:                  v.name,
    plate_number:          v.plate,
    type_name_snapshot:    v.type || '',
    vehicle_type_id:       v.typeId    ? parseInt(v.typeId)    : null,
    vehicle_group_id:      v.groupId   ? parseInt(v.groupId)   : null,
    agreement_template_id: v.agreementId ? parseInt(v.agreementId) : null,
    current_area_id:       v.currentAreaId ? parseInt(v.currentAreaId) : null,
    icon:                  v.icon || '',
    status:                v.status || 'available',
    seats_or_capacity:     v.seats || '',
    transmission:          v.transmission || null,
    fuel_type:             v.fuelType || null,
    features:              v.features || '',
    mileage_policy:        v.mileage || 'unlimited',
    price_day:             v.priceDay || 0,
    confirm_mode:          v.confirm || 'manual',
    deposit_default:       v.deposit || 0,
    odometer:              v.odometer || 0,
    mot_expiry_date:       v.motExpiry || null,
    is_bookable_online:    bool(v.bookPage),
    use_group_pricing:     bool(v.useGroupPricing),
    insurance_mode:        v.insuranceMode || 'standard',
    map_x:                 v.mapX ?? null,
    map_y:                 v.mapY ?? null,
    map_speed_label:       v.speedLabel || null,
    map_heading_label:     v.headingLabel || null,
    external_code:         v.externalCode || null,
  };
}

// ── Customers ─────────────────────────────────────────────────────────────────
export function customerFromApi(c) {
  return {
    id:          str(c.id),
    _backendId:  c.id,
    fname:       c.first_name || '',
    lname:       c.last_name  || '',
    phone:       c.phone      || '',
    email:       c.email      || '',
    address:     c.address    || '',
    passport:    c.passport_number || '',
    licence:     c.licence_number  || '',
    licExpiry:   isoDate(c.licence_expiry_date),
    dob:         isoDate(c.date_of_birth),
    nationality: c.nationality || '',
    notes:       c.notes      || '',
    color:       c.avatar_color || '#1a1a2e',
    since:       c.created_at
      ? new Date(c.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
      : '',
  };
}

export function customerToApi(c) {
  return {
    customer_code:        c.id || ('C-' + Date.now()),
    first_name:           c.fname || '',
    last_name:            c.lname || '',
    full_name:            `${c.fname || ''} ${c.lname || ''}`.trim(),
    phone:                c.phone || null,
    email:                c.email || null,
    address:              c.address || null,
    passport_number:      c.passport || null,
    licence_number:       c.licence || null,
    licence_expiry_date:  c.licExpiry || null,
    date_of_birth:        c.dob || null,
    nationality:          c.nationality || null,
    notes:                c.notes || null,
    avatar_color:         c.color || null,
  };
}

// ── Rentals ───────────────────────────────────────────────────────────────────
export function rentalFromApi(r) {
  const id = r.rental_code || str(r.id);
  return {
    id,
    _backendId:   r.id,
    vid:          str(r.vehicle_id),
    status:       r.status || 'booked',
    type:         r.type   || 'rental',
    source:       r.source || 'direct',
    customer:     r.customer_name || '',
    phone:        r.customer_phone || '',
    email:        r.customer_email || '',
    passport:     r.passport_number || '',
    licence:      r.licence_number  || '',
    customerId:   str(r.customer_id),
    bookingRef:   r.booking_reference || r.rental_code || str(r.id),
    start:        r.start_at || '',
    end:          r.end_at   || '',
    startLabel:   fmtDateLabel(r.start_at ? r.start_at.slice(0, 10) : ''),
    endLabel:     fmtDateLabel(r.end_at   ? r.end_at.slice(0, 10)   : ''),
    pickupLocId:   str(r.pickup_location_id),
    pickupLocName: r.pickup_location_name || '',
    returnLocId:   str(r.return_location_id),
    returnLocName: r.return_location_name || '',
    priceType:    r.price_type || 'day',
    rate:         num(r.rate),
    discount:     num(r.discount_amount),
    discType:     r.discount_type || 'eur',
    agreedPrice:  num(r.agreed_price),
    deposit:      num(r.deposit_expected),
    depositMethod: r.deposit_method || '',
    payMethod:    r.pay_method || '',
    notes:        r.notes || '',
    cond:         r.condition_pickup || 'no_damage',
    condNote:     r.condition_pickup_note || '',
    condRet:      r.condition_return || null,
    condRetNote:  r.condition_return_note || '',
    signed:       bool(r.is_signed),
    signaturePath: r.signature_path || '',
    assignedTo:   str(r.assigned_to_user_id),
    assignPhase:  null,
    isConfirmed:  bool(r.is_confirmed),
    bankTransferPending:  bool(r.bank_transfer_pending),
    bankTransferVerified: bool(r.bank_transfer_verified),
    createdAt:    r.created_at ? r.created_at.slice(0, 10) : '',
    // Lease-specific
    monthlyRate:    num(r.monthly_rate),
    termMonths:     r.term_months || null,
    billingCadence: r.billing_cadence || 'monthly',
    invoicedMonths: r.invoiced_months || [],
    vatRate:        num(r.vat_rate),
    vatMode:        r.vat_mode || 'inclusive',
    vatNumber:      r.customer_vat_number || '',
    customerCompany: r.customer_company || '',
    // Odometer
    pickupOdometer: r.pickup_odometer_km || null,
    returnOdometer: r.return_odometer_km || null,
  };
}

export function rentalToApi(r) {
  return {
    rental_code:          r.id || r.bookingRef || ('CNT-' + Date.now()),
    booking_reference:    r.bookingRef || null,
    vehicle_id:           r.vid        ? parseInt(r.vid)        : null,
    customer_id:          r.customerId ? parseInt(r.customerId) : null,
    agreement_template_id: r.agreementTemplateId ? parseInt(r.agreementTemplateId) : null,
    status:               r.status || 'booked',
    type:                 r.type   || 'rental',
    source:               r.source || 'direct',
    customer_name:        r.customer || '',
    customer_phone:       r.phone || null,
    customer_email:       r.email || null,
    customer_address:     r.address || null,
    customer_company:     r.customerCompany || null,
    customer_vat_number:  r.vatNumber || null,
    passport_number:      r.passport || null,
    licence_number:       r.licence  || null,
    start_at:             r.start || null,
    end_at:               r.end   || null,
    pickup_location_id:   r.pickupLocId ? parseInt(r.pickupLocId) : null,
    return_location_id:   r.returnLocId ? parseInt(r.returnLocId) : null,
    pickup_location_name: r.pickupLocName || null,
    return_location_name: r.returnLocName || null,
    price_type:           r.priceType || 'day',
    rate:                 r.rate     || 0,
    discount_amount:      r.discount || 0,
    discount_type:        r.discType || 'eur',
    agreed_price:         r.agreedPrice || 0,
    deposit_expected:     r.deposit || 0,
    deposit_method:       r.depositMethod || null,
    pay_method:           r.payMethod || null,
    notes:                r.notes || null,
    condition_pickup:     r.cond || 'no_damage',
    condition_pickup_note: r.condNote || null,
    is_confirmed:         bool(r.isConfirmed),
    is_signed:            bool(r.signed),
    // Lease
    monthly_rate:         r.monthlyRate || null,
    term_months:          r.termMonths  || null,
    billing_cadence:      r.billingCadence || null,
    vat_rate:             r.vatRate || null,
    vat_mode:             r.vatMode || null,
  };
}

// ── Maintenance Records ───────────────────────────────────────────────────────
export function maintFromApi(m) {
  return {
    id:          str(m.id),
    _backendId:  m.id,
    vid:         str(m.vehicle_id),
    type:        m.service_type || '',
    date:        isoDate(m.service_date),
    km:          m.odometer_km || 0,
    odomLabel:   m.display_odometer_label || '',
    cost:        num(m.cost),
    garage:      m.garage_name || '',
    nextDue:     m.next_due_label || '',
    nextDueKm:   m.next_due_km || null,
    nextDueDate: isoDate(m.next_due_date),
    trigger:     m.trigger_type || 'km',
    notifyKm:    m.notify_at_km || null,
    notifyDate:  isoDate(m.notify_at_date),
    notifyNote:  m.notification_note || '',
    notes:       m.notes || '',
    status:      m.status || 'complete',
    isNotified:  bool(m.is_notified),
    createdAt:   m.created_at ? m.created_at.slice(0, 10) : '',
  };
}

export function maintToApi(m) {
  return {
    vehicle_id:             m.vid ? parseInt(m.vid) : null,
    service_type:           m.type || '',
    service_date:           m.date || null,
    odometer_km:            m.km   || null,
    display_odometer_label: m.odomLabel || null,
    cost:                   m.cost || null,
    garage_name:            m.garage || null,
    next_due_label:         m.nextDue || null,
    next_due_km:            m.nextDueKm || null,
    next_due_date:          m.nextDueDate || null,
    trigger_type:           m.trigger || 'km',
    notify_at_km:           m.notifyKm || null,
    notify_at_date:         m.notifyDate || null,
    notification_note:      m.notifyNote || null,
    notes:                  m.notes || null,
    status:                 m.status || 'scheduled',
  };
}

// ── Invoices ──────────────────────────────────────────────────────────────────
export function invoiceFromApi(inv) {
  return {
    id:          str(inv.id),
    _backendId:  inv.id,
    num:         inv.invoice_number || '',
    status:      inv.status  || 'draft',
    issued:      inv.issue_date || '',
    issuedAt:    inv.created_at || '',
    issueDate:   inv.issue_date || '',
    dueDate:     inv.due_date  || '',
    due:         inv.due_date  || '',
    customer:    inv.customer_name  || '',
    customerCompany:   inv.customer_company    || null,
    customerVatNumber: inv.customer_vat_number || null,
    customerId:  str(inv.customer_id),
    vehicle:     inv.vehicle_name || '',
    vid:         str(inv.vehicle_id),
    contract:    inv.contract_reference || null,
    contractId:  str(inv.rental_id),
    method:      inv.payment_method || 'cash',
    methodLabel: inv.payment_method_label || inv.payment_method || 'Cash',
    lines:       (inv.lines || []).map(l => ({
      desc:  l.description || '',
      qty:   l.quantity || 1,
      unit:  num(l.unit_price),
      total: num(l.line_total),
    })),
    subtotal:    num(inv.subtotal),
    vatRate:     num(inv.vat_rate),
    vatAmt:      num(inv.vat_amount),
    vatMode:     inv.vat_mode || 'inclusive',
    discount:    num(inv.discount_amount),
    discType:    inv.discount_type || 'eur',
    total:       num(inv.total),
    notes:       inv.notes || '',
    isProForma:  bool(inv.is_pro_forma),
    isLeaseInvoice: bool(inv.is_lease_invoice),
    leaseMonthKey:  inv.lease_month_key || null,
    email:       inv.customer_email || '',
    source:      inv.source || '',
    paidAt:      inv.paid_at ? inv.paid_at.slice(0, 10) : null,
  };
}

export function invoiceToApi(inv) {
  return {
    rental_id:             inv.contractId ? parseInt(inv.contractId) : null,
    customer_id:           inv.customerId ? parseInt(inv.customerId) : null,
    vehicle_id:            inv.vid        ? parseInt(inv.vid)        : null,
    customer_name:         inv.customer  || '',
    customer_email:        inv.email     || null,
    customer_address:      null,
    customer_company:      inv.customerCompany    || null,
    customer_vat_number:   inv.customerVatNumber  || null,
    vehicle_name:          inv.vehicle   || null,
    invoice_number:        inv.num       || ('INV-' + Date.now()),
    contract_reference:    inv.contract  || null,
    payment_method:        inv.method    || 'cash',
    payment_method_label:  inv.methodLabel || null,
    issue_date:            inv.issueDate || inv.issued || new Date().toISOString().slice(0, 10),
    due_date:              inv.dueDate   || inv.due   || null,
    subtotal:              inv.subtotal  || 0,
    vat_rate:              inv.vatRate   || 0,
    vat_amount:            inv.vatAmt    || 0,
    discount_amount:       inv.discount  || 0,
    discount_type:         inv.discType  || 'eur',
    total:                 inv.total     || 0,
    status:                inv.status    || 'draft',
    notes:                 inv.notes     || null,
    is_pro_forma:          bool(inv.isProForma),
    source:                inv.source    || null,
    lines:                 (inv.lines || []).map(l => ({
      description: l.desc,
      quantity:    l.qty,
      unit_price:  l.unit,
      line_total:  l.total,
    })),
  };
}

// ── Damages ───────────────────────────────────────────────────────────────────
export function damageFromApi(d) {
  return {
    id:           str(d.id),
    _backendId:   d.id,
    vid:          str(d.vehicle_id),
    rentalId:     str(d.rental_id),
    customerId:   str(d.customer_id),
    reportedDate: isoDate(d.reported_at),
    createdAt:    d.created_at ? d.created_at.slice(0, 10) : '',
    desc:         d.description || '',
    location:     d.damage_location || '',
    severity:     d.severity || 'minor',
    status:       d.status   || 'reported',
    paidBy:       d.paid_by  || 'unpaid',
    repairCost:   num(d.repair_cost),
    photos:       (d.photos || []).map(p => (typeof p === 'string' ? p : p.url || p.path || '')),
    notes:        d.notes || '',
  };
}

export function damageToApi(d) {
  return {
    vehicle_id:      d.vid        ? parseInt(d.vid)        : null,
    rental_id:       d.rentalId   ? parseInt(d.rentalId)   : null,
    customer_id:     d.customerId ? parseInt(d.customerId) : null,
    description:     d.desc       || '',
    damage_location: d.location   || null,
    severity:        d.severity   || 'minor',
    status:          d.status     || 'reported',
    paid_by:         d.paidBy     || 'unpaid',
    repair_cost:     d.repairCost || null,
    notes:           d.notes      || null,
    reported_at:     d.reportedDate || new Date().toISOString().slice(0, 10),
  };
}

// ── Refunds ───────────────────────────────────────────────────────────────────
export function refundFromApi(r) {
  return {
    id:          str(r.id),
    _backendId:  r.id,
    rentalId:    str(r.rental_id),
    customerId:  str(r.customer_id),
    customer:    r.customer_name || '',
    amount:      num(r.amount),
    reason:      r.reason   || '',
    scenario:    r.scenario  || 'full',
    status:      r.status    || 'pending',
    method:      r.refund_method || 'bank',
    notes:       r.notes    || '',
    createdAt:   r.created_at   ? r.created_at.slice(0, 10)   : '',
    processedAt: r.processed_at ? r.processed_at.slice(0, 10) : null,
  };
}

// ── Date-change requests ──────────────────────────────────────────────────────
export function dateChangeRequestFromApi(d) {
  return {
    id:        str(d.id),
    _backendId: d.id,
    rentalId:  str(d.rental_id),
    customer:  d.customer_name || '',
    newStart:  isoDate(d.new_start_at),
    newEnd:    isoDate(d.new_end_at),
    reason:    d.reason  || '',
    status:    d.status  || 'pending',
    createdAt: d.created_at ? d.created_at.slice(0, 10) : '',
  };
}

// ── Notifications ─────────────────────────────────────────────────────────────
export function notificationFromApi(n) {
  return {
    id:    str(n.id),
    type:  n.type  || 'info',
    icon:  n.icon  || 'ℹ️',
    title: n.title || '',
    body:  n.body  || '',
    time:  n.created_at
      ? new Date(n.created_at).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })
      : '',
    read: n.read_at != null,
    link: n.link || null,
  };
}

// ── Roles ─────────────────────────────────────────────────────────────────────
export function roleFromApi(r) {
  const perms = {};
  if (Array.isArray(r.permissions)) {
    r.permissions.forEach(p => {
      perms[p.section] = { view: bool(p.can_view), edit: bool(p.can_edit) };
    });
  } else if (r.permissions && typeof r.permissions === 'object') {
    Object.assign(perms, r.permissions);
  } else if (r.perms) {
    Object.assign(perms, r.perms);
  }
  return {
    id:           str(r.id),
    _backendId:   r.id,
    name:         r.name || '',
    locked:       bool(r.is_locked),
    operatorMode: bool(r.operator_mode),
    perms,
  };
}

// ── Users ─────────────────────────────────────────────────────────────────────
export function userFromApi(u) {
  const nameParts = (u.name || '').split(' ');
  return {
    id:          str(u.id),
    _backendId:  u.id,
    fname:       u.first_name || nameParts[0] || '',
    lname:       u.last_name  || nameParts.slice(1).join(' ') || '',
    email:       u.email || '',
    roleId:      str(u.role_id),
    active:      u.active !== false,
    avatarColor: u.avatar_color || null,
    createdAt:   u.created_at ? u.created_at.slice(0, 10) : '',
  };
}

// ── Booking-setup catalog entities ────────────────────────────────────────────
export function locationFromApi(l) {
  return {
    id:      str(l.id),
    _backendId: l.id,
    name:    l.name    || '',
    areaId:  str(l.area_id),
    address: l.address || '',
    phone:   l.phone   || '',
  };
}

export function locationToApi(l) {
  return {
    name:    l.name,
    area_id: l.areaId ? parseInt(l.areaId) : null,
    address: l.address || null,
    phone:   l.phone   || null,
  };
}

export function areaFromApi(a) {
  return {
    id:    str(a.id),
    _backendId: a.id,
    name:  a.name  || '',
    color: a.color || '#888',
    emoji: a.emoji || '',
  };
}

export function groupFromApi(g) {
  return {
    id:          str(g.id),
    _backendId:  g.id,
    name:        g.name || '',
    priceDay:    num(g.price_day),
    deposit:     num(g.deposit),
    agreementId: str(g.agreement_template_id),
    description: g.description || '',
  };
}

export function vehicleTypeFromApi(t) {
  return {
    id:          str(t.id),
    _backendId:  t.id,
    name:        t.name || '',
    icon:        t.icon || '',
    description: t.description || '',
  };
}

export function rateRuleFromApi(r) {
  return {
    id:        str(r.id),
    _backendId: r.id,
    name:      r.name       || '',
    type:      r.rule_type  || 'duration',
    effect:    r.effect     || 'discount',
    value:     num(r.value),
    valueType: r.value_type || 'percent',
    minDays:   r.min_days   || null,
    dateStart: r.date_start || null,
    dateEnd:   r.date_end   || null,
    appliesTo: r.applies_to || 'all',
    active:    bool(r.active !== false),
  };
}

export function promoCodeFromApi(p) {
  return {
    id:        str(p.id),
    _backendId: p.id,
    code:      p.code    || '',
    discount:  num(p.discount_amount),
    discType:  p.discount_type || 'pct',
    minDays:   p.min_days || null,
    maxUses:   p.max_uses || null,
    uses:      p.uses_count || 0,
    active:    bool(p.active !== false),
    expiresAt: isoDate(p.expires_at),
  };
}

export function extraFromApi(e) {
  return {
    id:        str(e.id),
    _backendId: e.id,
    name:      e.name || '',
    price:     num(e.price),
    priceType: e.price_type || 'per_day',
    desc:      e.description || '',
    active:    bool(e.active !== false),
  };
}

export function insurancePlanFromApi(p) {
  return {
    id:     str(p.id),
    _backendId: p.id,
    name:   p.name || '',
    price:  num(p.price_per_day),
    desc:   p.description || '',
    active: bool(p.active !== false),
  };
}

export function poiFromApi(p) {
  return {
    id:   str(p.id),
    _backendId: p.id,
    name: p.name  || '',
    type: p.type  || '',
    lat:  p.lat   || null,
    lng:  p.lng   || null,
    desc: p.description || '',
  };
}

export function agreementTemplateFromApi(t) {
  return {
    id:        str(t.id),
    _backendId: t.id,
    name:      t.name      || '',
    body:      t.body_html || t.body || '',
    isDefault: bool(t.is_default),
    appliesTo: t.applies_to || 'both',
    createdAt: t.created_at ? t.created_at.slice(0, 10) : '',
  };
}

export function companySettingsFromApi(s) {
  if (!s) return null;
  return {
    ...s,
    bookingConfirmation: s.booking_confirmation || s.bookingConfirmation || 'manual',
    noShowDelayHours:    s.no_show_delay_hours  || s.noShowDelayHours  || 0,
    invPrefix:           s.invoice_prefix       || s.invPrefix         || 'INV-',
    invNext:             s.invoice_next_number  || s.invNext           || 1,
    vatRate:             s.vat_rate             || s.vatRate           || 19,
    vatMode:             s.vat_mode             || s.vatMode           || 'inclusive',
    companyName:         s.company_name         || s.companyName       || '',
    companyEmail:        s.company_email        || s.companyEmail      || '',
    companyPhone:        s.company_phone        || s.companyPhone      || '',
    companyAddress:      s.company_address      || s.companyAddress    || '',
    companyVat:          s.company_vat          || s.companyVat        || '',
    currency:            s.currency             || 'EUR',
  };
}
